/**
 * Cache-aware decay framework — pure decision logic (Layer 2 of 3).
 *
 * Hosts the three pure functions that drive `shouldTrim` decisions:
 *
 *   - {@link isInTurnSweepStep} — is this step a sweep tick?
 *   - {@link pickTier} — which tier ratio applies to a given value?
 *   - {@link shouldTrimByCacheSchedule} — combines the above into the
 *     "trim or not" verdict for a single payload.
 *
 * Every numeric value flows through a {@link ToolDecayConfig}; no
 * hard-coded thresholds live here. See ADR-052.
 */

import {
  type CacheScheduleConfig,
  DEFAULT_CACHE_SCHEDULE,
  type DecayTier,
  mergeSchedule,
  type ToolDecayConfig,
} from "./decay-config";
import type { ToolFormatterContext } from "./define";

/**
 * Whether the given in-turn step count is a sweep tick under the
 * provided ticks list (defaults to the global schedule's ticks).
 *
 * Sweep ticks are the only mid-turn steps on which formatters do
 * cache-breaking work. Between ticks, formatters return `{}` and the
 * apply helper short-circuits to a referentially-equal prompt.
 */
export function isInTurnSweepStep(
  inTurnStepCount: number,
  ticks: readonly number[] = DEFAULT_CACHE_SCHEDULE.inTurnSweepTicks
): boolean {
  for (const t of ticks) {
    if (t === inTurnStepCount) {
      return true;
    }
  }
  return false;
}

/**
 * Returns the highest-threshold tier whose `threshold <= value`,
 * scanning a tier list assumed sorted ASC by `threshold`. Returns
 * `undefined` when no tier matches (value below the lowest threshold).
 *
 * The tier table is the source of truth for tier ordering — this
 * function does not re-sort. Configs assemble tier arrays in ascending
 * order; tests pin the invariant.
 */
export function pickTier(
  value: number,
  tiers: readonly DecayTier[]
): number | undefined {
  let matchedRatio: number | undefined;
  for (const tier of tiers) {
    if (value >= tier.threshold) {
      matchedRatio = tier.ratio;
    } else {
      break;
    }
  }
  return matchedRatio;
}

/**
 * Decides whether a single payload should be trimmed under the
 * cache-aware schedule.
 *
 * Rules (in evaluation order):
 *
 *   0. **Absolute hard cap** — if `hardCapChars` is set and `charLen`
 *      exceeds it, trim immediately. Bypasses every gate below
 *      (stale-floor, sweep, turn schedule) so a single oversized
 *      payload can't blow up the context on the step it lands. See
 *      {@link getToolOutputHardCapChars}.
 *   1. **`minTrimSize` floor** — values at or below the floor are
 *      never trimmed, regardless of tier.
 *   2. **Turn-tier path** (turn transition) — if `turnDelta` activates
 *      a tier in `turnDeltaTiers`, compare `charLen` against
 *      `baseline * ratio`. Turn transitions trim regardless of
 *      `stepDelta` (so prior-turn bulk-trim works).
 *   3. **Stale-floor gate** — items with `stepDelta <
 *      staleFloorStepDelta` are never trimmed mid-turn.
 *   4. **Pressure tier** — if `contextFillRatio` activates a tier in
 *      `pressureTiers`, compare `charLen` against `baseline * ratio`
 *      on any step, bypassing the sweep gate.
 *   5. **In-turn sweep gate** — only sweep-tick steps proceed.
 *   6. **In-turn tick tier** — if `inTurnStepCount` activates a tier
 *      in `inTurnTickTiers`, compare `charLen` against
 *      `baseline * ratio`.
 *
 * On the turn-tier path the ratio is the tightest of the turn tier, the
 * pressure tier, and — when `cacheCold` — the deepest turn tier, since a
 * cold cache re-writes the prefix anyway.
 */
export function shouldTrimByCacheSchedule(opts: {
  charLen: number;
  stepDelta: number;
  turnDelta: number;
  inTurnStepCount: number;
  /** `ToolFormatterContext.contextFillRatio`; drives the pressure tiers. */
  contextFillRatio?: number;
  /** The prompt cache has expired since the previous turn. */
  cacheCold?: boolean;
  config: ToolDecayConfig;
  /**
   * Absolute char ceiling for a single payload, resolved from the call's
   * context window via {@link getToolOutputHardCapChars}. When set and
   * `charLen` exceeds it, the payload is trimmed unconditionally —
   * bypassing the cache-aware schedule and stale-floor. Omit to disable.
   */
  hardCapChars?: number;
}): boolean {
  // Rule 0: absolute hard cap — fires on the landing step, ignoring the
  // cache-friendly schedule, so one giant payload can't overflow the window.
  if (opts.hardCapChars != null && opts.charLen > opts.hardCapChars) {
    return true;
  }

  if (opts.charLen <= opts.config.minTrimSize) {
    return false;
  }

  const schedule: CacheScheduleConfig = mergeSchedule(opts.config);
  const pressureRatio =
    opts.contextFillRatio == null
      ? undefined
      : pickTier(opts.contextFillRatio, schedule.pressureTiers);

  // ─── Turn-tier path ─────────────────────────────────────────────
  const turnRatio = pickTier(opts.turnDelta, schedule.turnDeltaTiers);
  if (turnRatio !== undefined) {
    const coldRatio = opts.cacheCold
      ? schedule.turnDeltaTiers.at(-1)?.ratio
      : undefined;
    const ratio = Math.min(
      turnRatio,
      pressureRatio ?? turnRatio,
      coldRatio ?? turnRatio
    );
    return opts.charLen > opts.config.baseline * ratio;
  }

  // ─── In-turn sweep path ─────────────────────────────────────────
  if (opts.stepDelta < schedule.staleFloorStepDelta) {
    return false;
  }
  if (
    pressureRatio !== undefined &&
    opts.charLen > opts.config.baseline * pressureRatio
  ) {
    return true;
  }
  if (!isInTurnSweepStep(opts.inTurnStepCount, schedule.inTurnSweepTicks)) {
    return false;
  }

  const tickRatio = pickTier(opts.inTurnStepCount, schedule.inTurnTickTiers);
  if (tickRatio === undefined) {
    return false;
  }
  return opts.charLen > opts.config.baseline * tickRatio;
}

/** The schedule inputs every formatter forwards from its context. */
export function scheduleSignals(
  ctx: ToolFormatterContext
): Pick<
  Parameters<typeof shouldTrimByCacheSchedule>[0],
  | "stepDelta"
  | "turnDelta"
  | "inTurnStepCount"
  | "contextFillRatio"
  | "cacheCold"
> {
  return {
    stepDelta: ctx.stepDelta,
    turnDelta: ctx.turnDelta,
    inTurnStepCount: ctx.inTurnStepCount,
    contextFillRatio: ctx.contextFillRatio,
    cacheCold: ctx.cacheCold,
  };
}
